import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DeleteConfirmModal } from 'components/FormModal';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import { useDeletePromo, useGetPromos } from './gql/usePromoMutations';
import PromoFormModal from './PromoFormModal';

const PromoName = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 600;
`;

const TrackPromos = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editPromoId, setEditPromoId] = useState(null);
  const [deletePromoData, setDeletePromoData] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const { pathname } = useLocation();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch promos data
  const { data, loading, refetch } = useGetPromos();
  const { deletePromo, loading: deleteLoading } = useDeletePromo();

  const promos = data?.getPromosV2?.results || [];
  const count = data?.getPromosV2?.count || 0;

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle add promo button
  const handleAddPromo = () => {
    if (!capabilities || capabilities?.add_promos) {
      setEditPromoId(null);
      setShowFormModal(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  // Handle edit promo
  const handleEditPromo = (promo) => {
    setEditPromoId(promo.id);
    setShowFormModal(true);
  };

  // Handle delete promo
  const handleDeletePromo = async () => {
    if (!deletePromoData) return;

    const response = await deletePromo(deletePromoData.id);

    if (!response || response.errors) {
      toast.error('Error deleting discount');
    } else {
      toast.success('Discount successfully deleted');
      refetch();
    }

    setDeletePromoData(null);
  };

  // DataTable columns configuration
  const columns = [
    {
      field: 'name',
      header: 'Code Name',
      mobileTitle: true,
      render: (promo) => (
        <PromoName>
          <CellPrimary>{promo.name}</CellPrimary>
        </PromoName>
      ),
    },
    {
      field: 'expiration',
      header: 'Expiration',
      render: (promo) =>
        promo.no_expiration
          ? 'No Expiration'
          : promo.expiration
          ? moment(promo.expiration, 'MM-DD-YYYY')
              .format('ddd MMM D - YYYY')
              .toUpperCase()
          : 'None',
    },
    {
      field: 'ticket_discount',
      header: 'Ticket Discount',
      mobileSubtitle: true,
      render: (promo) => `${promo.ticket_discount}%`,
    },
    {
      field: 'service_discount',
      header: 'Fee Discount',
      render: (promo) => `${promo.service_discount}%`,
    },
    {
      field: 'limit',
      header: 'Use Limit',
      render: (promo) => promo.limit || 'Unlimited',
    },
    {
      field: 'status',
      header: 'Status',
      render: (promo) => (
        <Badge variant={promo.is_expired ? 'error' : 'success'}>
          {promo.is_expired ? 'Expired' : 'Active'}
        </Badge>
      ),
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'edit',
      title: 'Edit Discount',
      mobileLabel: 'Edit',
      onClick: handleEditPromo,
    },
    {
      type: 'delete',
      title: 'Delete Discount',
      mobileLabel: 'Delete',
      onClick: (promo) => setDeletePromoData(promo),
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="credits" />
            <HeaderTitle>Discounts</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search discounts..."
                debounceMs={500}
              />
              <ButtonV2
                onClick={handleAddPromo}
                style={{ width: 'auto', flexShrink: 0 }}
              >
                Add Discount
              </ButtonV2>
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={promos}
              loading={loading}
              keyField="id"
              emptyMessage="No discounts found"
              emptySubtext="Create your first discount code to get started"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>

        {/* Promo Form Modal */}
        <PromoFormModal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            setEditPromoId(null);
          }}
          promoId={editPromoId}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={!!deletePromoData}
          onClose={() => setDeletePromoData(null)}
          title="Delete Discount"
          message={`Are you sure you want to delete the discount "${deletePromoData?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeletePromo}
          isDeleting={deleteLoading}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/1`}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackPromos;
